import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import { IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import TableContainer from "@mui/material/TableContainer";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { visuallyHidden } from "@mui/utils";
import Paper from "@mui/material/Paper";
import Error from "../../Error";
import REACT_APP from "../../../environment";
import { Spin } from "antd";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: "id", numeric: false, label: "Id" },
    { id: "goal_name", numeric: true, label: "Goal Name" },
    { id: "year", numeric: true, label: "Year" },
    { id: "status", numeric: true, label: "Status" },
    { id: "goal_details", numeric: true, label: "Goal Details" },
    { id: "comments", numeric: true, label: "Comments" },
    { id: "action", numeric: true, label: "Action" },
];

function EnhancedTableHead(props) {
    const { t } = useTranslation();
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell key={headCell.id} align="left" sortDirection={orderBy === headCell.id ? order : false}>
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {t(headCell.label)}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

function CoreGoals({ empId }) {
    const { t } = useTranslation();
    const [orderBy, setOrderBy] = React.useState("calories");
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [errorMsg, setErrorMsg] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [order, setOrder] = React.useState("asc");
    const [data, setData] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [idx, setIdx] = React.useState("");
    const navigate = useNavigate();

    React.useEffect(() => {
        getDate();
    }, [empId]);

    const getDate = async () => {
        setErrorMsg(null);
        setLoading(true);
        axios
            .get(`${REACT_APP.URL}/api/hrp/v1/goal/performance?employee_id=${empId}`, {
                headers: { Authorization: sessionStorage.getItem("token") },
            })
            .then((res) => {
                setData(res.data.data.goals);
                setLoading(false);
            })
            .catch((err) => {
                setErrorMsg(err.message);
                setLoading(false);
            });
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [anchor, setAnchor] = React.useState(null);
    const [anchor2, setAnchor2] = React.useState(null);

    const openMenu = (event, id) => {
        setIdx(id);
        setAnchor(event.currentTarget);
    };

    const closeMenu = () => {
        setAnchor(null);
    };

    const approveStatus = () => {
        axios
            .patch(`${REACT_APP.URL}/api/hrp/v1/goal/performance/${idx.id}/status/approve`, {}, { headers: { Authorization: sessionStorage.getItem("token") } })
            .then((res) => {
                setAnchor2(null);
                setAnchor(null);
                toast.success(t("Success"));
                getDate();
            })
            .catch((err) => toast.error(t("Something went wrong")));
    };

    const gotoComments = (e, id) => {
        navigate(`/performance/${id}/details`);
    };

    return (
        <>
            <h5 className="text-center pb-3">{t("Core Goals")}</h5>
            {loading ? (
                <Spin style={{ marginTop: "65px" }} tip="Loading" size="large">
                    <div className="content" />
                </Spin>
            ) : errorMsg ? (
                <Error message={errorMsg} />
            ) : (
                // ) : data.length === 0 ? (
                //     <Empty />
                <div>
                    <Box sx={{ width: "100%" }}>
                        <Paper sx={{ width: "100%", mb: 2 }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 750, px: 2 }} aria-labelledby="tableTitle" size="medium">
                                    <EnhancedTableHead order={order} onRequestSort={handleRequestSort} orderBy={orderBy} rowCount={data.length} />
                                    <TableBody>
                                        {stableSort(data, getComparator(order, orderBy))
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row, index) => {
                                                return (
                                                    <TableRow hover tabIndex={-1} key={row.id}>
                                                        <TableCell align="left">{row.id}</TableCell>
                                                        <TableCell align="left">{row.goal_name}</TableCell>
                                                        <TableCell align="left">{row.year}</TableCell>
                                                        <TableCell align="left">{row.status}</TableCell>
                                                        <TableCell align="left">{row.goal_details}</TableCell>
                                                        <TableCell align="left">
                                                            <Button variant="contained" color="primary" onClick={(e, id = row.id) => gotoComments(e, id)}>
                                                                {t("View")}
                                                            </Button>
                                                        </TableCell>
                                                        <TableCell align="left" className="op_menu">
                                                            <Box sx={{ flexGrow: 0 }}>
                                                                <Tooltip title={t("Action")}>
                                                                    <IconButton onClick={(e, id = row) => openMenu(e, id)} sx={{ p: 0 }}>
                                                                        <MoreVertIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Menu
                                                                    sx={{ mt: "23px" }}
                                                                    id="menu-appbar"
                                                                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                                                                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                                                                    anchorEl={anchor}
                                                                    open={Boolean(anchor)}
                                                                    onClose={closeMenu}
                                                                >
                                                                    <MenuItem onClick={(e) => setAnchor2(e.currentTarget)} id="option">
                                                                        <Typography>{t("Status")}</Typography>
                                                                    </MenuItem>
                                                                </Menu>
                                                                <Menu
                                                                    sx={{ ml: "-77px", mt: "25px" }}
                                                                    id="menu-appbar"
                                                                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                                                    anchorEl={anchor2}
                                                                    open={Boolean(anchor2)}
                                                                    onClose={() => setAnchor2(null)}
                                                                >
                                                                    <MenuItem disabled={idx.status !== "submitted"} onClick={() => approveStatus()} id="option">
                                                                        <Typography>{t("Approve")}</Typography>
                                                                    </MenuItem>
                                                                </Menu>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                labelRowsPerPage={t("Rows per page")}
                                rowsPerPageOptions={[5, 10, 25, 100]}
                                count={data.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </Box>
                </div>
            )}
        </>
    );
}

export default React.memo(CoreGoals);
